import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { ReviewPolygon } from "@/types/document-review";
import { useTranslation } from "react-i18next";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configura il worker una sola volta
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerProps = {
    fileUrl: string;
    polygons: ReviewPolygon[];
    renderOverlay?: (args: {
        pageNumber: number;
        pageWidth: number;
        pageHeight: number;
        containerRef: React.RefObject<HTMLDivElement>;
    }) => React.ReactNode;
    onCreatePolygon?: (options?: { pageNumber?: number; x?: number; y?: number }) => void;
    onPageChange?: (page: number) => void;
    currentPage?: number; // Prop opzionale per controllare la pagina dall'esterno
    hideAddRectangle?: boolean; // Nasconde il pulsante "Aggiungi Rettangolo"
};

export function PdfViewer({ fileUrl, renderOverlay, onCreatePolygon, onPageChange, currentPage: controlledCurrentPage, hideAddRectangle = false }: PdfViewerProps) {
    const { t } = useTranslation('common');
    const [numPages, setNumPages] = useState<number>(0);
    const [internalCurrentPage, setInternalCurrentPage] = useState<number>(1);
    const [pageDimensions, setPageDimensions] = useState<Record<number, { width: number; height: number }>>({});
    const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; pageNumber: number; clientX: number; clientY: number } | null>(null);

    // Usa la prop controllata se fornita, altrimenti usa lo stato interno
    const currentPage = controlledCurrentPage ?? internalCurrentPage;
    const setCurrentPage = (page: number) => {
        setInternalCurrentPage(page);
        onPageChange?.(page);
    };

    const containerRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const addButtonRef = useRef<HTMLButtonElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handlePageRenderSuccess = (pageNumber: number) => {
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
            const container = containerRefs.current[pageNumber];
            if (container) {
                const width = container.clientWidth;
                const height = container.clientHeight;
                if (width > 0 && height > 0) {
                    setPageDimensions((prev) => ({
                        ...prev,
                        [pageNumber]: { width, height },
                    }));
                }
            }
        });
    };

    const handleCreateClick = () => {
        onCreatePolygon?.({ pageNumber: currentPage });
    };

    // Sincronizza lo stato interno quando cambia la prop controllata
    useEffect(() => {
        if (controlledCurrentPage !== undefined && controlledCurrentPage !== internalCurrentPage) {
            setInternalCurrentPage(controlledCurrentPage);
        }
    }, [controlledCurrentPage, internalCurrentPage]);

    // Notifica il cambiamento di pagina quando cambia lo stato interno o la prop
    useEffect(() => {
        onPageChange?.(currentPage);
    }, [currentPage, onPageChange]);


    useEffect(() => {
        if (!addButtonRef.current) return;

        // Usa IntersectionObserver per rilevare quando il pulsante esce dalla viewport
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Se il pulsante non è più visibile nella viewport, mostra la toolbar floating
                    setShowFloatingToolbar(!entry.isIntersecting);
                });
            },
            {
                // root: null usa la viewport come riferimento
                root: null,
                // rootMargin permette un piccolo margine prima di considerare il pulsante non visibile
                rootMargin: '0px',
                // threshold: 0 significa che vogliamo sapere quando anche solo 1px è fuori dalla viewport
                threshold: 0,
            }
        );

        observer.observe(addButtonRef.current);

        // Aggiungi anche un listener per lo scroll del container per una reattività migliore
        const handleScroll = () => {
            if (!addButtonRef.current) return;

            const rect = addButtonRef.current.getBoundingClientRect();
            // Controlla se il pulsante è fuori dalla viewport (sopra o sotto)
            const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
            setShowFloatingToolbar(!isVisible);
        };

        const scrollContainer = scrollContainerRef.current;
        scrollContainer?.addEventListener('scroll', handleScroll);
        window.addEventListener('scroll', handleScroll);

        // Check initial state
        handleScroll();

        return () => {
            observer.disconnect();
            scrollContainer?.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleContextMenu = (e: React.MouseEvent, pageNumber: number) => {
        e.preventDefault();
        const container = containerRefs.current[pageNumber];
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        setContextMenu({ x, y, pageNumber, clientX: e.clientX, clientY: e.clientY });
    };

    const handleCreatePolygonAtPosition = (x: number, y: number, pageNumber: number) => {
        onCreatePolygon?.({
            pageNumber,
            x: Math.max(0, Math.min(1, x - 0.084)),
            y: Math.max(0, Math.min(1, y - 0.0355)),
        });
        setContextMenu(null);
    };

    return (
        <div className="w-full h-full flex flex-col">
            {/* Barra paginazione fissa */}
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1}
                    >
                        {t('pdfViewer.prev')}
                    </button>
                    <span className="text-sm text-tertiary">{t('pdfViewer.page')} {currentPage}/{numPages || "-"}</span>
                    <button
                        className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50"
                        onClick={() => setCurrentPage(Math.min(numPages || currentPage, currentPage + 1))}
                        disabled={!numPages || currentPage >= numPages}
                    >
                        {t('pdfViewer.next')}
                    </button>
                </div>
                {!hideAddRectangle && (
                    <button
                        ref={addButtonRef}
                        className="px-3 py-1 rounded bg-brand-solid dark:text-white text-sm font-medium hover:bg-brand-solid_hover"
                        onClick={handleCreateClick}
                    >
                        + {t('pdfViewer.addRectangle')}
                    </button>
                )}
            </div>

            {/* Toolbar floating */}
            {showFloatingToolbar && (
                <div className="fixed top-16 left-0 lg:left-[296px] right-0 lg:right-[380px] z-50 bg-primary border-b border-secondary shadow-lg">
                    <div className="flex items-center justify-between px-4 py-2">
                        <div className="flex items-center gap-2">
                            <button
                                className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50"
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage <= 1}
                            >
                                {t('pdfViewer.prev')}
                            </button>
                            <span className="text-sm text-tertiary">{t('pdfViewer.page')} {currentPage}/{numPages || "-"}</span>
                            <button
                                className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50"
                                onClick={() => setCurrentPage(Math.min(numPages || currentPage, currentPage + 1))}
                                disabled={!numPages || currentPage >= numPages}
                            >
                                {t('pdfViewer.next')}
                            </button>
                        </div>
                        {!hideAddRectangle && (
                            <button
                                className="px-3 py-1 rounded bg-brand-solid dark:text-white text-sm font-medium hover:bg-brand-solid_hover"
                                onClick={handleCreateClick}
                            >
                                + {t('pdfViewer.addRectangle')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Area documento - mostra solo la pagina corrente */}
            <div ref={scrollContainerRef} className="flex-1 overflow-auto flex items-center justify-center">
                <Document file={fileUrl} onLoadSuccess={handleDocumentLoad} loading={<div className="p-6">{t('pdfViewer.loadingPdf')}</div>}>
                    {currentPage > 0 && currentPage <= numPages && (
                        <div
                            className="relative flex justify-center"
                            onContextMenu={(e) => handleContextMenu(e, currentPage)}
                        >
                            <div
                                ref={(el) => {
                                    containerRefs.current[currentPage] = el;
                                    // Update dimensions when ref is set and container is available
                                    if (el) {
                                        // Use requestAnimationFrame to ensure DOM is fully updated
                                        requestAnimationFrame(() => {
                                            const width = el.clientWidth;
                                            const height = el.clientHeight;
                                            if (width > 0 && height > 0) {
                                                setPageDimensions((prev) => ({
                                                    ...prev,
                                                    [currentPage]: { width, height },
                                                }));
                                            }
                                        });
                                    }
                                }}
                                className="relative"
                                style={{ width: "fit-content" }}
                            >
                                <Page
                                    pageNumber={currentPage}
                                    renderAnnotationLayer
                                    renderTextLayer
                                    onRenderSuccess={() => handlePageRenderSuccess(currentPage)}
                                />
                                {/* Overlay - only render if dimensions are available */}
                                {pageDimensions[currentPage] && (
                                    <div className="absolute inset-0 pointer-events-auto z-10">
                                        {renderOverlay?.({
                                            pageNumber: currentPage,
                                            pageWidth: pageDimensions[currentPage].width,
                                            pageHeight: pageDimensions[currentPage].height,
                                            containerRef: { current: containerRefs.current[currentPage] } as React.RefObject<HTMLDivElement>,
                                        })}
                                    </div>
                                )}
                                {/* Polygons sono gestiti dall'overlay (es. Fabric.js) */}
                            </div>
                        </div>
                    )}
                </Document>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setContextMenu(null)}
                    />
                    <div
                        className="fixed z-50 bg-primary border border-secondary rounded-lg shadow-lg p-1"
                        style={{
                            left: `${contextMenu.clientX}px`,
                            top: `${contextMenu.clientY}px`,
                        }}
                    >
                        <button
                            className="px-3 py-2 text-sm text-primary hover:bg-secondary rounded w-full text-left"
                            onClick={() => {
                                handleCreatePolygonAtPosition(contextMenu.x, contextMenu.y, contextMenu.pageNumber);
                            }}
                        >
                            {t('pdfViewer.addRectangleHere')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}


